// Ad-account drift: the kyck Account.fbAdAccountId (the Ad Builder's target)
// vs the Zernio profile's scopedAdAccountIds (what connectAds actually scoped
// the sync to). An AM can edit fbAdAccountId without re-running connectAds,
// leaving the Ad Builder targeting a DIFFERENT ad account than the profile is
// connected to — a publish-to-the-wrong-account hazard in a franchise network.
//
// kyck stores the id RAW (e.g. "1247…"); Zernio holds it act_-prefixed
// ("act_1247…"). Both sides MUST be normalized before comparing, or a
// raw-vs-prefixed string compare false-positives every account. Pure + shared
// by the api-connections drift flag, the publish guard, and tests.

export function normalizeAdAccountId(raw: string | number | null | undefined): string | null {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  return s.startsWith('act_') ? s : `act_${s}`;
}

// True when the account's ad account is set, the profile IS scoped, and the
// (normalized) account id is NOT among the scoped ids. When the account has no
// ad account, or the profile has no scope yet, returns false — there's no
// *changed-away-from-scope* drift to flag (and we never want to false-block a
// publish on "unknown scope" alone).
export function detectAdAccountDrift(
  fbAdAccountId: string | null | undefined,
  scopedAdAccountIds: string[] | null | undefined,
): boolean {
  const acct = normalizeAdAccountId(fbAdAccountId);
  if (!acct) return false;
  const scoped = (scopedAdAccountIds ?? [])
    .map(normalizeAdAccountId)
    .filter((v): v is string => v !== null);
  if (scoped.length === 0) return false;
  return !scoped.includes(acct);
}

// The two sides, normalized, for messaging (publish guard + drift chip). null
// when there's nothing to compare.
export function adAccountDriftSummary(
  fbAdAccountId: string | null | undefined,
  scopedAdAccountIds: string[] | null | undefined,
): { accountAdAccountId: string; scopedAdAccountIds: string[] } | null {
  if (!detectAdAccountDrift(fbAdAccountId, scopedAdAccountIds)) return null;
  return {
    accountAdAccountId: normalizeAdAccountId(fbAdAccountId)!,
    scopedAdAccountIds: (scopedAdAccountIds ?? [])
      .map(normalizeAdAccountId)
      .filter((v): v is string => v !== null),
  };
}

// ---------------------------------------------------------------------------
// Fleet-wide drift classification (the roster drift detector). Extends the
// per-account predicate above into a total classifier so the fleet report and
// the per-account chip share ONE definition. Pure — the caller resolves the
// profile from the zernioProfiles cache and passes its scoped ids.
// ---------------------------------------------------------------------------

export type DriftKind =
  // fbAdAccountId present AND in the profile's scoped set — nothing to fix.
  | 'in-sync'
  // fbAdAccountId blank, but the profile HAS ≥1 scoped ad account (the
  // ownership-change gap — the new owner's account is on the profile, the kyck
  // record never caught up). Fix: adopt the profile's ad account.
  | 'unset'
  // fbAdAccountId present but NOT among the profile's scoped ids (the classic
  // swap-to-a-different-account, or a typo). Fix: re-scope to the profile's.
  | 'drifted'
  // fbAdAccountId present, but the profile has NO scoped ad account (the Meta
  // connection dropped / the cached profile can't see one). Reconnect needed.
  | 'profile-disconnected'
  // The account has no zernioProfileId at all (onboarding gap — the existing
  // "not connected" set, primarily Coverage's concern).
  | 'no-profile';

export type AdAccountDriftClassification = {
  kind: DriftKind;
  // Normalized (act_…) kyck-side ad account, or null when unset.
  kyckAdAccountId: string | null;
  // Normalized (act_…) ad account(s) the profile is scoped to.
  profileAdAccountIds: string[];
};

// Classify one account's ad-account drift against its (already-resolved) Zernio
// profile. `profile` is the cached zernioProfiles entry (or null when the link
// points at nothing in the cache). Total + null-safe.
export function classifyAccountAdAccountDrift(
  account: { fbAdAccountId?: string | null; zernioProfileId?: string | null },
  profile: { scopedAdAccountIds?: string[] | null } | null,
): AdAccountDriftClassification {
  const kyckAdAccountId = normalizeAdAccountId(account.fbAdAccountId);
  const profileAdAccountIds = (profile?.scopedAdAccountIds ?? [])
    .map(normalizeAdAccountId)
    .filter((v): v is string => v !== null);

  if (!account.zernioProfileId) {
    return { kind: 'no-profile', kyckAdAccountId, profileAdAccountIds };
  }

  if (!kyckAdAccountId) {
    // Blank kyck ad account. Drift only when the profile HAS one to adopt;
    // blank-on-both-sides is nothing to fix (treated in-sync).
    return {
      kind: profileAdAccountIds.length > 0 ? 'unset' : 'in-sync',
      kyckAdAccountId,
      profileAdAccountIds,
    };
  }

  if (profileAdAccountIds.length === 0) {
    // kyck has an ad account but the profile shows none — connection dropped
    // (or the cache is behind; a profile sync clears a false positive).
    return { kind: 'profile-disconnected', kyckAdAccountId, profileAdAccountIds };
  }

  return {
    kind: profileAdAccountIds.includes(kyckAdAccountId) ? 'in-sync' : 'drifted',
    kyckAdAccountId,
    profileAdAccountIds,
  };
}

// The drift kinds that are actionable config-drift (the fleet worklist). Excludes
// in-sync (nothing wrong) and no-profile (the onboarding gap — Coverage's job).
export const ACTIONABLE_DRIFT_KINDS: readonly DriftKind[] = [
  'unset',
  'drifted',
  'profile-disconnected',
];

export function isActionableDrift(kind: DriftKind): boolean {
  return ACTIONABLE_DRIFT_KINDS.includes(kind);
}
